
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertTriangle, Eye, MessageSquare, Search, Filter, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";

interface SuspiciousActivity {
  id: string;
  user_id: string;
  activity_type: string;
  severity: string;
  details: Json;
  status: string;
  detected_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  admin_notes?: string;
  user_email?: string;
  user_name?: string;
}

export const SuspiciousActivitiesTab = () => {
  const [activities, setActivities] = useState<SuspiciousActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [selectedActivity, setSelectedActivity] = useState<SuspiciousActivity | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const { toast } = useToast();

  const fetchActivities = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('suspicious_activities')
        .select('id, user_id, activity_type, severity, details, status, detected_at, reviewed_at, reviewed_by, admin_notes')
        .order('detected_at', { ascending: false });

      if (error) throw error;

      // Get user profile information separately
      const userIds = [...new Set(data.map(activity => activity.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      const activitiesWithUserInfo = data.map(activity => {
        const profile = profileMap.get(activity.user_id);
        return {
          ...activity,
          user_email: profile?.email || 'Unknown',
          user_name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown User'
        };
      });

      setActivities(activitiesWithUserInfo);
    } catch (error) {
      console.error('Error fetching suspicious activities:', error);
      toast({
        title: "Error",
        description: "Failed to load suspicious activities",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleReviewActivity = async (activityId: string, newStatus: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('suspicious_activities')
        .update({
          status: newStatus,
          admin_notes: notes,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', activityId);

      if (error) throw error;

      toast({
        title: "Activity Updated",
        description: `Activity has been marked as ${newStatus}`,
      });

      fetchActivities();
      setReviewDialogOpen(false);
      setSelectedActivity(null);
      setAdminNotes("");
    } catch (error) {
      console.error('Error updating activity:', error);
      toast({
        title: "Error",
        description: "Failed to update activity",
        variant: "destructive"
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reviewed':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'dismissed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatActivityType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDetails = (details: Json) => {
    if (typeof details === 'object' && details !== null) {
      return Object.entries(details).map(([key, value]) => (
        `${key}: ${value}`
      )).join(', ');
    }
    return String(details);
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = 
      activity.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.activity_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || activity.status === statusFilter;
    const matchesSeverity = severityFilter === 'all' || activity.severity === severityFilter;
    
    return matchesSearch && matchesStatus && matchesSeverity;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading suspicious activities...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Suspicious Activities
          </CardTitle>
          <CardDescription>
            Monitor and review suspicious user activities and security alerts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user email, name, or activity type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activities Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Detected</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6">
                      <div className="text-muted-foreground">
                        {activities.length === 0 ? "No suspicious activities found" : "No activities match your filters"}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredActivities.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{activity.user_name}</p>
                          <p className="text-sm text-muted-foreground">{activity.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatActivityType(activity.activity_type)}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {formatDetails(activity.details)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSeverityColor(activity.severity)}>
                          {activity.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(activity.status)}>
                          {activity.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(activity.detected_at).toLocaleDateString()}
                          <br />
                          <span className="text-muted-foreground">
                            {new Date(activity.detected_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedActivity(activity);
                                setAdminNotes(activity.admin_notes || "");
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Review Suspicious Activity</DialogTitle>
                              <DialogDescription>
                                Activity detected on {new Date(activity.detected_at).toLocaleString()}
                              </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <strong>User:</strong> {activity.user_name} ({activity.user_email})
                                </div>
                                <div>
                                  <strong>Activity Type:</strong> {formatActivityType(activity.activity_type)}
                                </div>
                                <div>
                                  <strong>Severity:</strong> 
                                  <Badge className={`ml-2 ${getSeverityColor(activity.severity)}`}>
                                    {activity.severity}
                                  </Badge>
                                </div>
                                <div>
                                  <strong>Current Status:</strong> 
                                  <Badge className={`ml-2 ${getStatusColor(activity.status)}`}>
                                    {activity.status}
                                  </Badge>
                                </div>
                              </div>
                              
                              <div>
                                <strong>Details:</strong>
                                <div className="mt-1 p-3 bg-muted rounded-lg">
                                  <pre className="text-sm whitespace-pre-wrap">
                                    {JSON.stringify(activity.details, null, 2)}
                                  </pre>
                                </div>
                              </div>

                              <div>
                                <strong>Admin Notes:</strong>
                                <Textarea
                                  value={adminNotes}
                                  onChange={(e) => setAdminNotes(e.target.value)}
                                  placeholder="Add notes about this activity..."
                                  className="mt-1"
                                />
                              </div>
                            </div>

                            <DialogFooter className="gap-2">
                              <Button
                                variant="outline"
                                onClick={() => handleReviewActivity(activity.id, 'dismissed', adminNotes)}
                              >
                                Dismiss
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleReviewActivity(activity.id, 'reviewed', adminNotes)}
                              >
                                Mark as Reviewed
                              </Button>
                              <Button
                                onClick={() => handleReviewActivity(activity.id, 'resolved', adminNotes)}
                              >
                                Mark as Resolved
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
